import { Store } from "@ngxs/store";
import {
  AddCurrentBidData,
  RemoveCurrentBidData,
} from "./actions/currentBidData.actions";
import { CurrentBidDataState } from "../utility/states/currentBidData.state";
import { Inject, forwardRef, Injectable } from "@angular/core";
import { TblBidDataDisplay } from "../models/BidDataDisplay";
import { TblUserData } from "../models/UserData";
import { AddUserData, RemoveUserData } from "./actions/userData.action";
import {
  AddColumnDefinition,
  RemoveColumnDefinition,
} from "./actions/columnDefinition.action";
import { DataColumn } from "../models/ColumnDefintion";
import { ColumnDefinitionState } from "./states/columnDefinition.state";
import { UserDataState } from "./states/userData.state";
import { BidListDataState } from "./states/bidListData.state";
import {
  AddBidListData,
  RemoveBidListData,
} from "./actions/bidListData.action";
import { LineCalendarData } from "../models/CalendarData";
import { DexieOperations } from "./dexie-operation";
import { VacColumnDefinitionState } from "./states/vacColumnDefinition.state";
import { AddVacColumnDefinition, RemoveVacColumnDefinition } from "./actions/vacColumnDefinition.action";

@Injectable()
export class GlobalStore {
  constructor(
    @Inject(forwardRef(() => Store)) private store: Store,
    @Inject(forwardRef(() => DexieOperations)) private dexie: DexieOperations
  ) {}
  /* #region  Get global data(from store) */
  /**
   * @method getCurrentBidData get current store values for current bid data
   */
  public getCurrentBidData = (callBack?): TblBidDataDisplay => {
    const currentBidData =
      this.store.selectSnapshot(CurrentBidDataState).currentBidData;
    if (currentBidData == undefined) {
      const empNum = localStorage.getItem("empnum").replace(/[^\d.]/g, "");
      const keyCombination = localStorage.getItem("bidId");
      this.dexie.getCurrentBidData(empNum, keyCombination, (res) => {
        this.removeAddCurrentBid(res[0], true);
        if (callBack)
          callBack(
            this.store.selectSnapshot(CurrentBidDataState).currentBidData
          );
        else
          return this.store.selectSnapshot(CurrentBidDataState).currentBidData;
      });
    } else {
      if (callBack)
        callBack(this.store.selectSnapshot(CurrentBidDataState).currentBidData);
      else return this.store.selectSnapshot(CurrentBidDataState).currentBidData;
    }
  };

  /**
   * @method getSelectedColumnDefinition get selected column definition values for current bid data
   */
  public getSelectedColumnDefinition = (): DataColumn[] => {
    return this.store.selectSnapshot(ColumnDefinitionState).colDef;
  };

    /**
   * @method getVacationColumnDefinition get selected vacation column definition values for current bid data
   */
    public getVacationColumnDefinition = (): DataColumn[] => {
      return this.store.selectSnapshot(VacColumnDefinitionState).VacColDef;
    };

  /**
   * @method getUserData for getting the user data
   */
  public getUserData = (): TblUserData => {
    return this.store.selectSnapshot(UserDataState).userData;
  };

  /**
   * @method getBidListData for getting the bid list data
   */
  public getBidListData = (): LineCalendarData[] => {
    return this.store.selectSnapshot(BidListDataState).bidListData;
  };
  /* #endregion */

  /* #region  Remove and add global data (store operation)*/

  /**
   * @method removeAddUserData For removing /adding User Data to db store
   * @param UserData parameter for inserting into user data store as object
   * @param isNeedInsert bool value for checkng whether we need to insert into store
   */
  public removeAddUserData = (
    UserData?: TblUserData,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveUserData);
      if (isNeedInsert && UserData) {
        this.store.dispatch(new AddUserData(UserData));
      }
    } catch (error) {
      throw `ClassName:GlobalStore, FunctionName: removeAddUserData, ErrorMessage: ${error.stack.toString()}`;
    }
  };

  /**
   * @method removeAddColumnDefinition For removing /adding Column Definitions to db store
   * @param ColumnDefinition parameter for inserting into Column Definitions object
   * @param isNeedInsert bool value for checkng whether we need to insert into store
   */
  public removeAddColumnDefinition = (
    ColumnDefinition?: DataColumn[],
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveColumnDefinition);
      if (isNeedInsert && ColumnDefinition.length > 0) {
        this.store.dispatch(new AddColumnDefinition(ColumnDefinition));
      }
    } catch (error) {
      throw `ClassName:GlobalStore, FunctionName: removeAddColumnDefinition, ErrorMessage: ${error.stack.toString()}`;
    }
  };

   /**
   * @method removeAddVacationColumnDefinition For removing /adding WbidMax vacation Column Definitions to db store
   * @param VacationColumnDefinition parameter for inserting into Column Definitions object
   * @param isNeedInsert bool value for checkng whether we need to insert into store
   */
   public removeAddVacationColumnDefinition = (
    VacationColumnDefinition?: DataColumn[],
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveVacColumnDefinition);
      if (isNeedInsert && VacationColumnDefinition.length > 0) {
        this.store.dispatch(new AddVacColumnDefinition(VacationColumnDefinition));
      }
    } catch (error) {
      throw `ClassName:GlobalStore, FunctionName: removeAddVacationColumnDefinition, ErrorMessage: ${error.stack.toString()}`;
    }
  };

  /**
   * @method removeAddCurrentBid For removing /adding current bid from/to db store
   * @param currentBidData parameter for inserting into current bid store as biddatadisplay object
   * @param isNeedInsert bool value for checkng whether we need to insert into store
   */
  public removeAddCurrentBid = (
    currentBidData?: TblBidDataDisplay,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveCurrentBidData);
      if (isNeedInsert && currentBidData) {
        this.store.dispatch(new AddCurrentBidData(currentBidData));
      }
    } catch (error) {
      throw `ClassName:GlobalStore, FunctionName: removeAddCurrentBid, ErrorMessage: ${error.stack.toString()}`;
    }
  };

  /**
   * @method removeAddBidListData For removing /adding bid list data from/to db store
   * @param currentBidData parameter for inserting into current bid store as biddatadisplay object
   * @param isNeedInsert bool value for checkng whether we need to insert into store
   */
  public removeAddBidListData = (
    bidListData?: LineCalendarData[],
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveBidListData);
      if (isNeedInsert && bidListData) {
        this.store.dispatch(new AddBidListData(bidListData));
      }
    } catch (error) {
      throw `ClassName:GlobalStore, FunctionName: removeAddBidListData, ErrorMessage: ${error.stack.toString()}`;
    }
  };
  /* #endregion */
}
